import React, { Fragment, useEffect, useState } from "react";
import { useEventWizard } from "pages/asset/events/shared/EventWizardContext";
import { FormRadio } from 'components/form-fields';
import { FormDragAndDropFileUploader } from 'components/form-fields/FormDragAndDropFileUploader';
import { GridLayout } from "components/grid/GridLayout";
import { FleetService, BlobStorageService } from "services";
import { ListingTable } from 'components/listing';
import { AlertConfirm, NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { TextButton, Button } from "components/buttons";
import { UserAccess } from "components/user-access";
import { Tooltip } from "components/tooltip";
import { ShowHide } from "components/layout";
import { EventAddLaterOptions } from "pages/asset/events/modals/DefaultModels";

export function DocumentEvent({
    mode
}) {
    
    const { 
        event,
        setEvent,
        eventType,
        stepsConfig, 
        setStepsConfig,
        loadingData, 
        setLoadingData,
        saving,
        setSaving,
        setStepIsValid,
        setHasMadeChanges,
        setActiveStep 
    } = useEventWizard();

    const costDocumentationTypeId = 2;
    const eventDocumentationTypeId = 3;
    const isDocumentMode = mode === "document";
    const documentationTypeId = isDocumentMode ? eventDocumentationTypeId : costDocumentationTypeId;
    const documentationType = isDocumentMode ? "event" : "cost";
    //We'll set this to true intially to avoid screen flicker
    const [addLater, setAddLater] = useState(true);

    const editAction = "FMS:Edit";
    const columnButtonWidth = "50px";
    const columnWidths = [null, null, columnButtonWidth];

    const config = isDocumentMode ? stepsConfig.DocumentEvent : stepsConfig.CostDocumentEvent;
    const { addDocumentsLater, addCostDocumentsLater } = config;
    
    const [stepData, setStepData] = useState({
        documents: []
    });

    const isLoading = saving || loadingData;
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [fileDownloading, setFileDownloading] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);

    // File upload
    const maxFileSize = 26214400;
    const allowedFileTypes = [
        'application/pdf', 
        'image/png', 
        'image/jpeg', 
        'image/jpg', 
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/csv','application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    // Table
    const [tableData, setTableData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const tableHeaders = [
        "File Name",
        "File Type",
        "Delete"
    ];

    useEffect(() => {
        setActiveStep(mode === "cost" ? "CostDocument" : "Document");
        setAddLater(mode === "cost" ? config.addCostDocumentsLater : config.addDocumentsLater);
    }, []);

    function onRadioFieldChange(item) {
        const value = item.currentTarget.value === "true";
        const fieldName = isDocumentMode ? "addDocumentsLater" : "addCostDocumentsLater";

        if (isDocumentMode) {
            setStepsConfig(prev => ({
                ...prev,
                DocumentEvent: {
                    ...prev.DocumentEvent,
                    [fieldName]: value
                }
            }));
        } else {
            setStepsConfig(prev => ({
                ...prev,
                CostDocumentEvent: {
                    ...prev.CostDocumentEvent,
                    [fieldName]: value
                }
            }));
        }

        setAddLater(value);
    }

    const handleApiError = (err, message) => {
        const validationError = 400;

        if (err?.response?.status === validationError && err.response.data != null) {
            NotificationToaster.show(
                Intent.WARNING,
                err.response.data
            );
            return;
        } 

        NotificationToaster.show(
            Intent.DANGER,
            message
        );
    };

    function onFilesSelected(files) {
        if (files.length === 0) {
            return;
        }

        const filePromises = [];

        files.forEach(async (fileData) => {
            filePromises.push(FleetService.addEventDocument(event.eventId, fileData, documentationTypeId)
                .then(() => { setHasMadeChanges(true); })
                .catch((err) => handleApiError(err, "Could not add your event document, please try again."))
            );
        });

        Promise.all(filePromises)
            .then(() => {
                refreshEvent();
            })
            .finally(() => {
                setSaving(false); 
            });
    }

    function removeDocumentFromUploadList(fileName) {
        setNewDocuments((prevFiles) => prevFiles.filter(f => f.file.name !== fileName));
    }

    function refreshEvent() {
        setLoadingData(true);

        FleetService.getEvent(event.eventId)
            .then((updatedEvent) => {
                setEvent(updatedEvent);
            })
            .finally(() => {
                setLoadingData(false);
            });
    }

    useEffect(() => {
        const eventDocuments = event?.documents?.filter(doc => doc.type.toLowerCase() === documentationType) || [];

        setStepData({ documents: eventDocuments });
        mapEventDocumentsToTable(eventDocuments);
        setTotalRecords(eventDocuments.length);
    }, [event, addLater]);

    function throwGetFileError() {
        setFileDownloading(false);
        NotificationToaster.show(Intent.DANGER, "Unable to download file", false);
    }

    function onClickDownloadEventDocument(eventDocument) {
        FleetService.getEventDocumentLink(event.eventId, eventDocument.eventDocumentationId)
            .then((response) => {
                NotificationToaster.show(Intent.SUCCESS, "Your file will be downloaded shortly", false);

                BlobStorageService.downloadFile(
                    `https://${response.storageName}.blob.core.windows.net`,
                    response.containerName,
                    response.fileName, 
                    response.token, 
                    response.downloadName
                )
                    .then(() => {
                        setFileDownloading(false);
                    })
                    .catch(() => {
                        throwGetFileError();
                    });
            })
            .catch(() => {
                throwGetFileError();
            });
    }

    function mapEventDocumentsToTable(eventDocuments) {
        const rows = eventDocuments.map((eventDocument) => [
            <TextButton 
                onClick={() => onClickDownloadEventDocument(eventDocument)} 
                key={`download-event-document-${eventDocument.eventDocumentationId}`}
                disabled={fileDownloading || isLoading}
            >
                {`${eventDocument.name}`}
            </TextButton>,
            eventDocument.type,
            <UserAccess
                key={eventDocument.eventDocumentationId}
                perform={editAction}
                yes={() => (
                    <Tooltip key={`delete-${eventDocument.eventDocumentationId}`} content={`Delete`}>
                        <Button
                            text="Delete"
                            intent={Intent.DANGER}
                            onClick={() => {
                                setShowDeleteDialog(true);
                                setDocumentToDelete(eventDocument);
                            }}
                            icon="trash"
                            iconOnly={true}
                            minimal={true}
                            large={false}
                            disabled={addLater}
                        />
                    </Tooltip>
                )}
                no={() => (event.eventTypeName)}
            />
        ]);
        setTableData(rows);
    }

    function onDocumentDelete() {
        setLoadingData(true);

        FleetService.deleteEventDocument(event.eventId, documentToDelete.eventDocumentationId)
            .then(() => {
                setHasMadeChanges(true);
                NotificationToaster.show(Intent.SUCCESS, "Document deleted successfully.");
                setShowDeleteDialog(false);
            })
            .catch((err) => handleApiError(err, "Unable to delete the document, please try again."))
            .finally(() => {
                refreshEvent();
            });
    }

    function getFileInputHelperText() {

        return (
            <Fragment>
                <ShowHide
                    evaluator={isDocumentMode && eventType !== null && eventType !== undefined && eventType.documentationHelperText !== null && eventType.documentationHelperText !== undefined}
                    show={<p>{eventType?.documentationHelperText}</p>}
                />
                <p>File formats accepted are .pdf, .jpg, .jpeg, .png, .doc, .csv and .xls</p>
            </Fragment>
        );
    }

    useEffect(() => {
        if (isDocumentMode) {
            let isDocumentCountValid = stepData?.documents?.length >= eventType?.requiresDocumentationCount;
            setStepIsValid(!config.documentsAreRequired || addDocumentsLater || isDocumentCountValid);
        } else {
            let isDocumentCountValid = stepData?.documents?.length >= eventType?.requiresCostDocumentationCount;
            setStepIsValid(!config.documentsAreRequired || addCostDocumentsLater || isDocumentCountValid);
        }
    }, [eventType, addDocumentsLater, addCostDocumentsLater, stepData]);

    return (
        <div>
            <h2>Add {isDocumentMode ? "Documents" : "Cost Documents"}</h2>

            <ShowHide
                evaluator={
                    addLater ||
                    (config.documentsAreRequired &&
                    event.documents.filter(x => x.type.toLowerCase() === documentationType).length < eventType.requiresCostDocumentationCount)
                }
                show={
                    <FormRadio
                        disabled={saving}
                        options={EventAddLaterOptions}
                        onChange={(item) => onRadioFieldChange(item)}
                        headingText={`Add ${isDocumentMode ? "documents" : "cost documents"} later`}
                        selectedValue={addLater}
                        loading={loadingData}
                        id="input-add-documents-later"
                    />
                }
            />

            <ShowHide 
                evaluator={!addLater}
                show={(
                    <>
                        <ShowHide
                            evaluator={config.documentsAreRequired}
                            show={(
                                <h4>Please upload the required number of {isDocumentMode ? "documents" : "cost documents"} ({stepData?.documents?.length}/{isDocumentMode ? eventType.requiresDocumentationCount : eventType.requiresCostDocumentationCount})</h4>
                            )}
                            hide={(
                                <h4>No {isDocumentMode ? "documents" : "cost documents"} are required for this event, but you may upload some if needed.</h4>
                            )}
                        />
                    
                        <GridLayout numberOfColumns={1}>
                            <FormDragAndDropFileUploader
                                onChange={(files) => onFilesSelected(files)}
                                disabled={loadingData || saving}
                                loading={loadingData}
                                smaller={true}
                                headingText={getFileInputHelperText()}
                                documents={[]}
                                onRemoveDocument={removeDocumentFromUploadList}
                                maxFileSize={maxFileSize}
                                allowedFileTypes={allowedFileTypes}
                                showNewVersionsAsWarning={true}
                                versionWarningText={"One or more documents share their name with existing documents. Uploading these will create a new versions of the documents."}
                            />
                        </GridLayout>

                        <h2>Uploaded {isDocumentMode ? "Documents" : "Cost Documents"} ({stepData?.documents?.length})</h2>

                        <ListingTable
                            id="listing-table-asset-inprogress-events"
                            headers={tableHeaders}
                            data={tableData}
                            columnWidths={columnWidths}
                            totalRecordCount={totalRecords}
                            loadingData={loadingData}
                            pageable={false}
                        />
                    </>
                )}
            />

            <AlertConfirm
                title="Please confirm"
                isOpen={showDeleteDialog}
                onConfirm={onDocumentDelete}
                onCancel={() => {
                    setShowDeleteDialog(false)
                    setDocumentToDelete(null);
                }}
            >
                <p>Are you sure you want to delete this document?</p>
            </AlertConfirm>
        </div>
    );
}
